import { logger } from '../utils/logger';
/**
 * Build Version Service
 * Provides access to current build version for API headers and WebSocket params
 */

let cachedVersion: string | null = null;
let cachedVersionInfo: any = null;

/**
 * Get build version string
 * @returns Promise<string> Version string in format: {branch}-yyMMdd-{number}
 */
export async function getBuildVersion(): Promise<string> {
  if (cachedVersion) {
    return cachedVersion;
  }

  // Use Vite's base URL to ensure correct path in all environments
  const base = import.meta.env.BASE_URL || '/';
  const timestamp = Date.now(); // Force cache bust
  
  try {
    // Try to load from version.json first (includes metadata)
    const url = `${base}version.json?t=${timestamp}`;
    const response = await fetch(url, { 
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' }
    });
    if (response.ok) {
      const data = await response.json();
      cachedVersion = data.version;
      cachedVersionInfo = data;
      logger.log('📦 Build version loaded:', cachedVersion);
      return cachedVersion;
    }
  } catch (err: any) {
    logger.warn('⚠️ Could not load version.json:', err.message);
  }

  try {
    // Fallback to .version file
    const url = `${base}.version?t=${timestamp}`;
    const response = await fetch(url, { 
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' }
    });
    if (response.ok) {
      const version = await response.text();
      cachedVersion = version.trim();
      if (cachedVersion) {
        logger.log('📦 Build version loaded from .version:', cachedVersion);
        return cachedVersion;
      }
    }
  } catch (err: any) {
    logger.warn('⚠️ Could not load .version file:', err.message);
  }

  // Fallback to unknown version
  cachedVersion = 'unknown-000000-0';
  logger.warn('⚠️ Using fallback version:', cachedVersion);
  return cachedVersion;
}

/**
 * Get full version info (if available)
 * @returns Promise<object|null> Version info object with version, timestamp, branch, date
 */
export async function getVersionInfo(): Promise<any> {
  if (cachedVersionInfo) {
    return cachedVersionInfo;
  }

  const base = import.meta.env.BASE_URL || '/';
  const timestamp = Date.now();
  
  try {
    const url = `${base}version.json?t=${timestamp}`;
    const response = await fetch(url, { 
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' }
    });
    if (response.ok) {
      cachedVersionInfo = await response.json();
      cachedVersion = cachedVersionInfo.version;
      return cachedVersionInfo;
    }
  } catch (err: any) {
    logger.warn('⚠️ Could not load version info:', err.message);
  }

  return null;
}

/**
 * Get cached version (synchronous, returns null if not loaded yet)
 * @returns string|null Cached version string or null
 */
export function getCachedVersion(): string | null {
  return cachedVersion;
}

/**
 * Clear version cache (for testing/refresh)
 */
export function clearVersionCache(): void {
  cachedVersion = null;
  cachedVersionInfo = null;
}

/**
 * Initialize version (load on app start)
 * Call this in main.tsx
 */
export async function initVersion(): Promise<void> {
  try {
    await getBuildVersion();
    logger.log('✅ Build version initialized');
  } catch (err) {
    logger.error('❌ Failed to initialize build version:', err);
  }
}

export default {
  getBuildVersion,
  getVersionInfo,
  getCachedVersion,
  clearVersionCache,
  initVersion
};

